'use client';

import { useEffect, useRef } from 'react';
import NotificationCenter from '@/components/notifications/notification-center';

type NotificationDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export default function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const closeButtonRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    closeButtonRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      closeButtonRef.current?.focus();
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 h-full w-full cursor-default bg-black/55"
        onClick={onClose}
        aria-label="Close notifications"
      />
      <aside
        id="notification-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        className="relative ml-auto h-full w-full border-l border-border bg-background shadow-2xl sm:w-[420px]"
      >
        <NotificationCenter variant="drawer" active={open} onClose={onClose} />
      </aside>
    </div>
  );
}
